import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import styles from './AboutSection.module.css';

gsap.registerPlugin(ScrollTrigger);

const bgImage = require('../images/afriat-a-house 1.webp');

const AboutSection = () => {
  const sectionRef = useRef(null);
  const bgRef = useRef(null);
  const greetRef = useRef(null);
  const nameRef = useRef(null);
  const roleRef = useRef(null);
  const dividerRef = useRef(null);
  const paragraphsRef = useRef([]);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Background image - subtle parallax
      gsap.to(bgRef.current, {
        y: -60,
        ease: 'none',
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top bottom',
          end: 'bottom top',
          scrub: true,
        },
      });

      // Greeting text
      gsap.fromTo(greetRef.current,
        { opacity: 0, y: 30, clipPath: 'inset(100% 0 0 0)' },
        {
          opacity: 1, y: 0, clipPath: 'inset(0% 0 0 0)',
          duration: 1, ease: 'power3.out',
          scrollTrigger: {
            trigger: greetRef.current,
            start: 'top 85%',
            toggleActions: 'play none none reverse',
          },
        }
      );

      // Name
      gsap.fromTo(nameRef.current,
        { opacity: 0, y: 20 },
        {
          opacity: 1, y: 0,
          duration: 0.9, ease: 'power3.out',
          scrollTrigger: {
            trigger: nameRef.current,
            start: 'top 88%',
            toggleActions: 'play none none reverse',
          },
        }
      );

      // Role
      gsap.fromTo(roleRef.current,
        { opacity: 0, y: 15 },
        {
          opacity: 1, y: 0,
          duration: 0.8, ease: 'power3.out',
          scrollTrigger: {
            trigger: roleRef.current,
            start: 'top 90%',
            toggleActions: 'play none none reverse',
          },
        }
      );

      // Divider line
      gsap.fromTo(dividerRef.current,
        { scaleX: 0 },
        {
          scaleX: 1,
          duration: 0.9, ease: 'power3.inOut',
          scrollTrigger: {
            trigger: dividerRef.current,
            start: 'top 90%',
            toggleActions: 'play none none reverse',
          },
        }
      );

      // Paragraphs - scroll-scrub color reveal
      paragraphsRef.current.forEach((p, i) => {
        if (!p) return;
        // Entrance fade-in
        gsap.fromTo(p,
          { opacity: 0, y: 18 },
          {
            opacity: 1, y: 0,
            duration: 0.8, ease: 'power3.out',
            scrollTrigger: {
              trigger: p,
              start: 'top 92%',
              toggleActions: 'play none none reverse',
            },
          }
        );
        // Scrub-driven color reveal - text goes from faded to readable
        const isClosing = i === paragraphsRef.current.length - 1;
        ScrollTrigger.create({
          trigger: p,
          start: 'top 80%',
          end: 'top 40%',
          scrub: 0.4,
          onUpdate: (self) => {
            if (self.progress > 0.5) {
              p.classList.add(styles.paragraphRevealed);
              if (isClosing) p.classList.add(styles.closingLineRevealed);
            } else {
              p.classList.remove(styles.paragraphRevealed);
              if (isClosing) p.classList.remove(styles.closingLineRevealed);
            }
          },
        });
      });

      // Magazine parallax - greeting moves slower
      gsap.to(greetRef.current, {
        y: -30,
        ease: 'none',
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top bottom',
          end: 'bottom top',
          scrub: true,
        },
      });

      // Paragraphs subtle stagger parallax
      paragraphsRef.current.forEach((p, i) => {
        if (!p) return;
        gsap.to(p, {
          y: -(10 + i * 6),
          ease: 'none',
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top bottom',
            end: 'bottom top',
            scrub: true,
          },
        });
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section className={styles.section} ref={sectionRef} id="about">
      {/* Background image */}
      <div className={styles.bgImageWrap} ref={bgRef}>
        <img src={bgImage} alt="אפריאט אדריכלות - אודות המשרד" className={styles.bgImage} loading="lazy" decoding="async" />
      </div>
      <div className={styles.bgOverlay} />

      <div className={styles.inner}>
        <div className={styles.header}>
          <span className={styles.greeting} ref={greetRef}>אודות המשרד</span>
          <h2 className={styles.name} ref={nameRef}>AFRIAT ARCHITECTURE</h2>
          <span className={styles.role} ref={roleRef}>אדריכלות ועיצוב פנים</span>
          <div className={styles.divider} ref={dividerRef} />
        </div>

        <p
          className={styles.paragraph}
          ref={(el) => (paragraphsRef.current[0] = el)}
        >
          אנחנו מאמינים שאדריכלות טובה מתחילה בתחושה.
          לא רק בצורה, לא רק בחומר - אלא באופן שבו מרחב פוגש את החיים עצמם.
          בכל פרויקט, המטרה היא לייצר מקום מדויק. כזה שמרגיש טבעי למי שחי בו.
        </p>
        <p
          className={styles.paragraph}
          ref={(el) => (paragraphsRef.current[1] = el)}
        >
          אדריכלות היא יצירת חוויה שלמה - מערכת מדויקת של אור, חומר, פרופורציה ושקט.
          מרחב שנועד לא רק להיראות נכון, אלא להרגיש נכון בכל רגע שחיים בו.
          <span className={styles.highlight}> כל פרויקט נולד מתוך הקשבה עמוקה לאנשים שעתידים לחיות בו.</span>
          אנחנו מחפשים את האיזון שבין ניקיון צורני לחום אנושי, בין אסתטיקה על-זמנית לנוחות יומיומית.
        </p>
        <p
          className={styles.paragraph}
          ref={(el) => (paragraphsRef.current[2] = el)}
        >
          לאורך השנים ליווינו פרויקטים פרטיים ומסחריים מתוך תפיסה מוקפדת ובלתי מתפשרת - מהקו הראשוני ועד הפרט האחרון.
          כל החלטה מתקבלת מתוך מחשבה על האופן שבו החלל יתפקד, ינוע ויתיישן עם הזמן.
          הגישה שלנו שואפת לייצר בתים מדויקים, רגועים ועל-זמניים.
          חללים שמאפשרים לאור, לחומר ולחיים עצמם לקבל מקום.
        </p>
      </div>
    </section>
  );
};

export default AboutSection;
